import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, RefreshCw, Camera } from 'lucide-react';
import ReceiptStatsBar from '@/components/receipts/ReceiptStatsBar';
import ReceiptsList from '@/components/receipts/ReceiptsList';
import PerJobReceiptsCard from '@/components/receipts/PerJobReceiptsCard';
import LogReceiptDialog from '@/components/receipts/LogReceiptDialog';
import {
  fetchReceiptsData,
  flattenReceipts,
  computeReceiptStats,
  applyReceiptFilter,
  loadStoreRebateRates,
  migrateInlineReceiptPhotos,
} from '@/lib/receiptsData';

export default function ReceiptScannerPage() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [storeFilter, setStoreFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [logOpen, setLogOpen] = useState(false);
  const [defaultJobId, setDefaultJobId] = useState('');
  const migrateRanRef = useRef(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      // Tenant store-rebate rates first, so rebate math/UI use the right %.
      const [allJobs] = await Promise.all([
        fetchReceiptsData(),
        loadStoreRebateRates(),
      ]);
      setJobs(allJobs);
      // One-time lazy cleanup: move legacy inline photos to the storage
      // bucket so bloated rows (esp. the Misc/Gas buckets) shrink back to a
      // size that saves fast. Silent + best-effort; refresh if it did work.
      if (!migrateRanRef.current) {
        migrateRanRef.current = true;
        migrateInlineReceiptPhotos(allJobs)
          .then((n) => {
            if (n > 0) fetchReceiptsData().then(setJobs).catch(() => {});
          })
          .catch(() => {});
      }
    } catch (e) {
      setError(e?.message || t('receiptScanner.loadError'));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const allRows = useMemo(() => flattenReceipts(jobs), [jobs]);
  const stats = useMemo(() => computeReceiptStats(allRows), [allRows]);
  const filteredRows = useMemo(
    () =>
      applyReceiptFilter(allRows, {
        search,
        store: storeFilter,
        jobId: jobFilter,
      }),
    [allRows, search, storeFilter, jobFilter]
  );

  const handleJobUpdated = (updatedJob) => {
    if (!updatedJob?.id) return;
    setJobs((cur) => {
      const exists = cur.some((j) => j.id === updatedJob.id);
      return exists
        ? cur.map((j) => (j.id === updatedJob.id ? { ...j, ...updatedJob } : j))
        : [updatedJob, ...cur]; // e.g. a freshly-created Miscellaneous bucket
    });
  };

  const handlePickJob = (jobId) => {
    setJobFilter(jobId);
  };

  const handleAddReceipt = () => {
    setDefaultJobId('');
    setLogOpen(true);
  };

  return (
    <div data-testid="receipt-scanner-page">
      <PageHeader
        eyebrow={t('receiptScanner.eyebrow')}
        title={t('receiptScanner.title')}
        subtitle={t('receiptScanner.subtitle')}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => load({ silent: true })}
              disabled={loading || refreshing}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="receipts-refresh"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {t('receiptScanner.refresh')}
            </Button>
            <Button
              size="sm"
              onClick={handleAddReceipt}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="receipts-add"
            >
              <Camera size={14} className="mr-2" />
              {t('receiptScanner.captureReceipt')}
            </Button>
          </div>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="receipts-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <ReceiptStatsBar stats={stats} />

      {/* The By Job panel sits beside the list only from 2xl: beside a
          ~550px list at xl it squeezed the job-name column to "Post-F…". */}
      <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
        <div className="2xl:col-span-2">
          <ReceiptsList
            rows={filteredRows}
            loading={loading}
            storeFilter={storeFilter}
            onStoreFilter={setStoreFilter}
            jobFilter={jobFilter}
            onJobFilter={setJobFilter}
            jobsForFilter={jobs}
            search={search}
            onSearch={setSearch}
            jobs={jobs}
            onChanged={handleJobUpdated}
          />
        </div>
        <div>
          <PerJobReceiptsCard rows={stats.byJob} onPick={handlePickJob} />
        </div>
      </div>

      <LogReceiptDialog
        open={logOpen}
        onOpenChange={setLogOpen}
        jobs={jobs.filter((j) => j.status === 'active')}
        defaultJobId={defaultJobId}
        onLogged={handleJobUpdated}
      />
    </div>
  );
}
